/* CORSAILLE COMMERCE V2 — PRODUCT CONTROLLER */
(() => {
  'use strict';

  const $ = (selector, root = document) => root.querySelector(selector);
  const $$ = (selector, root = document) => Array.from(root.querySelectorAll(selector));
  const normalize = (value = '') => value
    .toString()
    .trim()
    .toLocaleLowerCase('ro-RO')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '');

  const money = (cents, currency = 'RON') => {
    try {
      return new Intl.NumberFormat('ro-RO', {
        style: 'currency',
        currency,
        maximumFractionDigits: 0,
      })
        .format((Number(cents) || 0) / 100)
        .replace(/\s?RON/g, ' lei');
    } catch (_) {
      return `${Math.round((Number(cents) || 0) / 100).toLocaleString('ro-RO')} lei`;
    }
  };


  const prefersReducedMotion = () => matchMedia('(prefers-reduced-motion: reduce)').matches;
  const parseLocaleNumber = (value) => {
    const normalized = String(value ?? '').trim().replace(/\s+/g, '').replace(',', '.');
    return normalized === '' ? NaN : Number(normalized);
  };

  function visibleHeaderOffset() {
    const candidates = $$([
      '.shopify-section-group-header-group',
      '.section-header',
      '.header-wrapper',
      '[data-header-wrapper]',
    ].join(','));
    return Math.max(0, ...candidates.map((node) => {
      const rect = node.getBoundingClientRect();
      const style = getComputedStyle(node);
      if (style.display === 'none' || style.visibility === 'hidden' || rect.height <= 0) return 0;
      if (rect.bottom <= 0 || rect.top >= Math.min(innerHeight, 320)) return 0;
      return Math.min(rect.bottom, 220);
    }));
  }

  function syncProductViewport(root, reevaluateSticky = true) {
    const headerOffset = Math.round(visibleHeaderOffset());
    root.style.setProperty('--cv2-header-offset', `${headerOffset}px`);
    $$('.cv2-identity__facts,.cv2-assurance', root).forEach((rail) => {
      const keyboardScrollable = innerWidth < 750 && rail.scrollWidth > rail.clientWidth + 2;
      if (keyboardScrollable) rail.tabIndex = 0;
      else rail.removeAttribute('tabindex');
    });
    const identity = $('.cv2-identity', root);
    if (!identity) return;

    // Sticky eligibility is recalculated only when geometry changes. Toggling it
    // while scrolling can make the identity column jump as a retracting header
    // changes the available height by a few pixels.
    if (reevaluateSticky || typeof root.cv2StickyEligible !== 'boolean') {
      const conservativeHeader = Math.max(headerOffset, 96);
      const available = innerHeight - conservativeHeader - 34;
      // The revolution layout deliberately separates the title from commerce
      // so mobile can place the jewellery between them. A partial sticky region
      // would strand price/actions without the product name, so only legacy
      // single-panel identities are eligible for sticky positioning.
      const splitIdentity = Boolean($('.cv2-identity-lead', root));
      root.cv2StickyEligible = !splitIdentity && innerWidth >= 1100 && identity.scrollHeight <= available;
    }
    identity.classList.toggle('cv2-identity--sticky', Boolean(root.cv2StickyEligible));
  }

  class CorsailleMediaGallery extends HTMLElement {
    connectedCallback() {
      if (this.dataset.cv2Ready === 'true') return;

      // Custom elements can connect before their descendants are parsed when a
      // merchant/app changes script loading. Defer briefly until the stage and
      // media nodes exist, but never enter an endless animation-frame loop for
      // a legitimate zero-media product.
      if ($('.cv2-gallery__empty', this)) {
        this.dataset.cv2Ready = 'true';
        this.classList.add('is-ready');
        return;
      }
      if (!$('.cv2-gallery__stage', this) || !this.allItems.length) {
        this.cv2ConnectAttempts = (this.cv2ConnectAttempts || 0) + 1;
        if (this.cv2ConnectAttempts <= 12 && !this.cv2ConnectQueued) {
          this.cv2ConnectQueued = true;
          requestAnimationFrame(() => {
            this.cv2ConnectQueued = false;
            if (this.isConnected) this.connectedCallback();
          });
        } else if (this.cv2ConnectAttempts > 12) {
          this.dataset.cv2Ready = 'true';
          this.classList.add('is-ready');
        }
        return;
      }
      this.cv2ConnectAttempts = 0;

      this.dataset.cv2Ready = 'true';
      this.abortController = new AbortController();
      this.signal = this.abortController.signal;
      this.variantMediaBridge = window.CorsailleVariantMediaBridgeV1?.readGallery?.(this) || null;
      this.index = 0;
      this.activeMediaId = '';
      this.configurationGroup = this.dataset.currentConfigurationGroup || '';
      this.touchStart = null;
      this.bind();
      this.mobileModeQuery = matchMedia('(max-width: 749px)');
      this.mobileModeQuery.addEventListener('change', () => {
        this.index = 0;
        this.setActiveMedia(this.activeMediaId || this.items[0]?.dataset.mediaId, false);
      }, { signal: this.signal });
      this.railObserver = new ResizeObserver(() => this.updateRailControls());
      const rail = $('.cv2-gallery__thumbs', this);
      if (rail) {
        this.railObserver.observe(rail);
        rail.addEventListener('scroll', () => this.updateRailControls(), { passive: true, signal: this.signal });
      }

      const currentVariantId = this.dataset.currentVariant || '';
      const initiallyActive = this.allItems.find((item) => item.classList.contains('is-active'));
      this.applyVariantFilter(currentVariantId);
      this.setActiveMedia(initiallyActive?.dataset.mediaId || this.items[0]?.dataset.mediaId, false);
      this.classList.add('is-ready');
    }

    disconnectedCallback() {
      this.railObserver?.disconnect();
      this.abortController?.abort();
      this.classList.remove('is-ready');
      delete this.dataset.cv2Ready;
      this.cv2ConnectAttempts = 0;
      this.cv2ConnectQueued = false;
    }

    get allItems() {
      return $$(':scope > .cv2-gallery__stage > .cv2-gallery__list > .cv2-gallery__item', this);
    }

    get items() {
      return this.allItems.filter((item) => !item.hidden);
    }

    get modalItems() {
      return this.items.filter((item) => item.dataset.mediaType === 'image');
    }

    get allThumbs() {
      return $$('[data-cv2-thumb]', this);
    }

    get thumbs() {
      return this.allThumbs.filter((thumb) => !thumb.closest('li')?.hidden);
    }

    get mode() {
      const product = this.closest('[data-corsaille-product-v2]');
      const mobileFocus = product?.classList.contains('cv2-product--mobile-gallery-focus')
        && matchMedia('(max-width: 749px)').matches;
      if (mobileFocus) return 'focus';
      return this.dataset.mode === 'diptych' ? 'diptych' : 'focus';
    }

    get shouldFilterVariantMedia() {
      return this.dataset.filterVariantMedia === 'true';
    }

    get pageStarts() {
      const count = this.items.length;
      if (count <= 1 || this.mode === 'focus') return Array.from({ length: count }, (_, index) => index);

      const starts = [];
      for (let index = 0; index < count; index += 2) {
        const start = index === count - 1 ? Math.max(0, count - 2) : index;
        if (!starts.includes(start)) starts.push(start);
      }
      return starts;
    }

    bind() {
      const signal = this.signal;
      $('[data-cv2-gallery-prev]', this)?.addEventListener('click', () => this.move(-1), { signal });
      $('[data-cv2-gallery-next]', this)?.addEventListener('click', () => this.move(1), { signal });
      $('[data-cv2-thumbs-prev]', this)?.addEventListener('click', () => this.scrollThumbs(-1), { signal });
      $('[data-cv2-thumbs-next]', this)?.addEventListener('click', () => this.scrollThumbs(1), { signal });

      this.addEventListener('click', (event) => {
        const thumb = event.target.closest('[data-cv2-thumb]');
        if (thumb && this.contains(thumb)) {
          this.setActiveMedia(thumb.dataset.mediaTarget, false);
          return;
        }
        const zoom = event.target.closest('[data-cv2-gallery-open]');
        if (zoom) {
          const item = zoom.closest('.cv2-gallery__item');
          if (item?.dataset.mediaId) this.setActiveMedia(item.dataset.mediaId, false);
          this.openModal();
        }
      }, { signal });

      const stage = $('.cv2-gallery__stage', this);
      stage?.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }, { passive: true, signal });
      stage?.addEventListener('touchend', (event) => {
        if (!this.touchStart) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - this.touchStart.x;
        const dy = touch.clientY - this.touchStart.y;
        this.touchStart = null;
        if (Math.abs(dx) > 46 && Math.abs(dx) > Math.abs(dy) * 1.25) this.move(dx < 0 ? 1 : -1);
      }, { passive: true, signal });

      $('.cv2-gallery__list', this)?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.move(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.move(1);
        }
      }, { signal });

      const modal = $('[data-cv2-gallery-modal]', this);
      $('[data-cv2-gallery-close]', this)?.addEventListener('click', () => modal?.close(), { signal });
      $('[data-cv2-modal-prev]', this)?.addEventListener('click', () => this.moveModal(-1), { signal });
      $('[data-cv2-modal-next]', this)?.addEventListener('click', () => this.moveModal(1), { signal });
      modal?.addEventListener('click', (event) => {
        if (event.target === modal) modal.close();
      }, { signal });
      modal?.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          this.moveModal(-1);
        } else if (event.key === 'ArrowRight') {
          event.preventDefault();
          this.moveModal(1);
        }
      }, { signal });
      modal?.addEventListener('touchstart', (event) => {
        const touch = event.touches[0];
        this.modalTouchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
      }, { passive: true, signal });
      modal?.addEventListener('touchend', (event) => {
        if (!this.modalTouchStart) return;
        const touch = event.changedTouches[0];
        if (!touch) return;
        const dx = touch.clientX - this.modalTouchStart.x;
        const dy = touch.clientY - this.modalTouchStart.y;
        this.modalTouchStart = null;
        if (Math.abs(dx) > 54 && Math.abs(dx) > Math.abs(dy) * 1.25) this.moveModal(dx < 0 ? 1 : -1);
      }, { passive: true, signal });
      modal?.addEventListener('close', () => {
        const target = this.modalReturnFocus;
        this.modalReturnFocus = null;
        if (target?.isConnected) target.focus({ preventScroll: true });
      }, { signal });
    }

    variantIdsFor(item) {
      return (item.dataset.variantIds || '')
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
    }

    configurationGroupFor(metal = '', stone = '') {
      if (this.dataset.configurationMediaMode !== 'epure-blue-v1') return '';

      const metalValue = normalize(metal);
      const stoneValue = normalize(stone);
      const metalKey = metalValue.includes('aur alb')
        || metalValue.includes('white')
        || metalValue.includes('platin')
        ? 'white'
        : 'yellow';
      const stoneKey = stoneValue.includes('incolor')
        || stoneValue.includes('colorless')
        || stoneValue.includes('white')
        || stoneValue === 'alb'
        ? 'white'
        : 'blue';
      return `${metalKey}-${stoneKey}`;
    }

    applyMediaBridgeVariant(variantId = '') {
      const api = window.CorsailleVariantMediaBridgeV1;
      const suite = api?.suiteFor?.(this.variantMediaBridge, variantId);
      if (!suite) return null;

      const allItems = this.allItems;
      const itemPlan = api.orderedNodePlan(allItems, suite, (item) => item.dataset.cvmbMediaId);
      if (!itemPlan) return null;

      const allThumbs = this.allThumbs;
      const thumbPlan = allThumbs.length
        ? api.orderedNodePlan(allThumbs, suite, (thumb) => String(thumb.dataset.mediaTarget || '').split('-').pop())
        : { selected: [], remainder: [] };
      if (!thumbPlan) return null;

      const selectedIds = new Set(suite.mediaIds);
      const list = $('.cv2-gallery__list', this);
      if (!list) return null;
      list.append(...itemPlan.selected, ...itemPlan.remainder);

      const thumbList = $('.cv2-gallery__thumbs', this);
      if (thumbList && allThumbs.length) {
        thumbList.append(
          ...thumbPlan.selected.map((thumb) => thumb.closest('li')).filter(Boolean),
          ...thumbPlan.remainder.map((thumb) => thumb.closest('li')).filter(Boolean),
        );
      }

      this.allItems.forEach((item, index) => {
        const visible = selectedIds.has(String(item.dataset.cvmbMediaId || ''));
        item.hidden = !visible;
        item.style.order = '';
        item.dataset.mediaIndex = String(index);
      });
      this.allThumbs.forEach((thumb) => {
        const id = String(thumb.dataset.mediaTarget || '').split('-').pop();
        const wrapper = thumb.closest('li');
        if (wrapper) {
          wrapper.hidden = !selectedIds.has(id);
          wrapper.style.order = '';
        }
      });
      thumbPlan.selected.forEach((thumb, index) => {
        thumb.setAttribute('aria-label', `Afișează imaginea ${index + 1} din ${suite.mediaIds.length}`);
      });

      this.dataset.currentVariant = String(variantId || '');
      this.dataset.currentConfigurationGroup = '';
      this.index = 0;
      this.updateControls();
      return suite;
    }

    applyVariantFilter(variantId = '') {
      const allItems = this.allItems;
      if (!allItems.length) return;

      if (this.variantMediaBridge) {
        this.applyMediaBridgeVariant(variantId);
        return;
      }
      // Liquid already rendered a safe selected suite. If client validation and
      // server validation ever disagree, freeze that suite instead of exposing
      // media from another metal family.
      if (this.dataset.cvmbActive === 'true') return;

      const configurationItems = this.configurationGroup
        ? allItems.filter((item) => !item.dataset.configurationGroup
          || item.dataset.configurationGroup === this.configurationGroup)
        : allItems;
      let visibleItems = configurationItems.length ? configurationItems : allItems;

      if (this.shouldFilterVariantMedia && variantId) {
        const variantItems = visibleItems.filter((item) => {
          const variantIds = this.variantIdsFor(item);
          return variantIds.length === 0 || variantIds.includes(String(variantId));
        });
        if (variantItems.length) visibleItems = variantItems;
      }

      const visibleIds = new Set(visibleItems.map((item) => item.dataset.mediaId));
      allItems.forEach((item) => {
        item.hidden = !visibleIds.has(item.dataset.mediaId);
      });
      this.allThumbs.forEach((thumb) => {
        const wrapper = thumb.closest('li');
        if (wrapper) wrapper.hidden = !visibleIds.has(thumb.dataset.mediaTarget);
      });

      this.dataset.currentVariant = String(variantId || '');
      this.dataset.currentConfigurationGroup = this.configurationGroup || '';
      this.index = Math.min(this.index || 0, Math.max(0, this.items.length - 1));
      this.updateControls();
    }

    setVariant(variantId, featuredMediaId = '', configuration = {}) {
      if (this.variantMediaBridge) {
        const suite = this.applyMediaBridgeVariant(String(variantId || ''));
        if (!suite) return;
        const heroItem = this.allItems.find((item) => item.dataset.cvmbMediaId === suite.heroId);
        this.setActiveMedia(heroItem?.dataset.mediaId || this.items[0]?.dataset.mediaId, false);
        return;
      }
      if (this.dataset.cvmbActive === 'true') return;

      const previousId = this.items[this.index]?.dataset.mediaId || this.activeMediaId;
      const previousGroup = this.configurationGroup || '';
      const nextGroup = this.configurationGroupFor(configuration.metal, configuration.stone);
      if (nextGroup) this.configurationGroup = nextGroup;
      const groupChanged = Boolean(nextGroup && nextGroup !== previousGroup);

      this.applyVariantFilter(String(variantId || ''));
      const featuredIsVisible = Boolean(featuredMediaId && this.indexForMedia(featuredMediaId) >= 0);
      const previousIsVisible = Boolean(!groupChanged && previousId && this.indexForMedia(previousId) >= 0);
      const targetId = featuredIsVisible
        ? featuredMediaId
        : previousIsVisible
          ? previousId
          : this.items[0]?.dataset.mediaId;
      this.setActiveMedia(targetId, false);
    }

    move(direction) {
      const starts = this.pageStarts;
      if (starts.length <= 1) return;
      const currentPage = Math.max(0, starts.indexOf(this.index));
      const nextPage = (currentPage + direction + starts.length) % starts.length;
      this.index = starts[nextPage];
      this.activeMediaId = this.items[this.index]?.dataset.mediaId || '';
      this.paint();
    }

    moveModal(direction) {
      const items = this.modalItems;
      if (items.length <= 1) return;
      let currentIndex = items.findIndex((item) => item.dataset.mediaId === this.activeMediaId);
      if (currentIndex < 0) currentIndex = 0;
      const nextIndex = (currentIndex + direction + items.length) % items.length;
      this.setActiveMedia(items[nextIndex]?.dataset.mediaId, false);
    }

    indexForMedia(mediaId) {
      return this.items.findIndex((item) => item.dataset.mediaId === mediaId);
    }

    diptychStartFor(index) {
      const starts = this.pageStarts;
      if (!starts.length) return 0;
      if (starts.includes(index)) return index;
      const containing = starts.filter((start) => index >= start && index <= start + 1);
      return containing.length ? containing[containing.length - 1] : starts[0];
    }

    setActiveMedia(mediaId, prepend = false) {
      if (!mediaId) {
        this.index = 0;
        this.paint();
        return;
      }

      let index = this.indexForMedia(mediaId);
      if (index < 0) index = 0;
      this.index = this.mode === 'diptych' ? this.diptychStartFor(index) : index;
      this.activeMediaId = this.items[index]?.dataset.mediaId || this.items[this.index]?.dataset.mediaId || '';

      if (prepend && this.index > 0 && this.mode === 'focus') {
        const item = this.items[this.index];
        item?.parentElement?.prepend(item);
        this.index = 0;
        this.activeMediaId = item?.dataset.mediaId || '';
      }
      this.paint();
    }

    visibleIndices() {
      if (!this.items.length) return [];
      if (this.mode !== 'diptych' || this.items.length === 1) return [this.index];
      return [this.index, Math.min(this.index + 1, this.items.length - 1)].filter((value, index, list) => list.indexOf(value) === index);
    }

    pauseMedia(item) {
      $$('video', item).forEach((video) => video.pause?.());
      $$('iframe', item).forEach((frame) => {
        try {
          frame.contentWindow?.postMessage(JSON.stringify({ event: 'command', func: 'pauseVideo', args: [] }), '*');
        } catch (_) {
          // Third-party players may not support postMessage control.
        }
      });
      $$('model-viewer', item).forEach((model) => model.pause?.());
    }

    paint() {
      const items = this.items;
      if (!items.length) {
        this.updateControls();
        return;
      }

      const starts = this.pageStarts;
      if (!starts.includes(this.index)) this.index = starts[0] || 0;
      const visibleIndices = this.visibleIndices();
      const visibleMediaIds = new Set(visibleIndices.map((index) => items[index]?.dataset.mediaId).filter(Boolean));
      if (!visibleMediaIds.has(this.activeMediaId)) this.activeMediaId = items[this.index]?.dataset.mediaId || '';

      this.allItems.forEach((item) => {
        const visible = !item.hidden && visibleMediaIds.has(item.dataset.mediaId);
        const active = !item.hidden && item.dataset.mediaId === this.activeMediaId;
        item.classList.toggle('is-active', active);
        item.classList.toggle('is-visible', visible);
        item.setAttribute('aria-hidden', visible ? 'false' : 'true');
        item.inert = !visible;
        if (!visible) this.pauseMedia(item);
      });

      this.allThumbs.forEach((thumb) => {
        const active = thumb.dataset.mediaTarget === this.activeMediaId;
        const paired = visibleMediaIds.has(thumb.dataset.mediaTarget);
        thumb.classList.toggle('is-active', active);
        thumb.classList.toggle('is-paired', paired && !active);
        thumb.setAttribute('aria-current', active ? 'true' : 'false');
        if (active) {
          const rail = thumb.closest('.cv2-gallery__thumbs');
          if (rail) {
            const left = Math.max(0, thumb.offsetLeft - (rail.clientWidth - thumb.offsetWidth) / 2);
            rail.scrollTo({ left, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
          }
        }
      });

      this.updateControls();
      if ($('[data-cv2-gallery-modal]', this)?.open) this.refreshModal();
    }

    updateControls() {
      const items = this.items;
      const starts = this.pageStarts;
      const hasNavigation = starts.length > 1;
      $$('[data-cv2-gallery-prev],[data-cv2-gallery-next]', this).forEach((button) => {
        button.hidden = !hasNavigation;
        button.disabled = !hasNavigation;
      });

      const railWrap = $('.cv2-gallery__rail-wrap', this);
      if (railWrap) railWrap.hidden = items.length <= 1;

      const modalItems = this.modalItems;
      $$('[data-cv2-modal-prev],[data-cv2-modal-next]', this).forEach((button) => {
        button.hidden = modalItems.length <= 1;
        button.disabled = modalItems.length <= 1;
      });
      const modalTotal = $('[data-cv2-modal-total]', this);
      const modalCounter = $('.cv2-gallery-modal__counter', this);
      if (modalTotal) modalTotal.textContent = String(modalItems.length);
      if (modalCounter) modalCounter.hidden = modalItems.length <= 1;

      const current = $('[data-cv2-gallery-current]', this);
      const total = $('[data-cv2-gallery-total]', this);
      const counter = $('.cv2-gallery__counter', this);
      if (counter) counter.hidden = items.length <= 1;
      if (total) total.textContent = String(items.length);
      this.updateRailControls();
      if (current) {
        const visible = this.visibleIndices();
        const first = (visible[0] ?? 0) + 1;
        const last = (visible[visible.length - 1] ?? visible[0] ?? 0) + 1;
        current.textContent = first === last ? String(first) : `${first}–${last}`;
      }
    }

    updateRailControls() {
      const rail = $('.cv2-gallery__thumbs', this);
      const prev = $('[data-cv2-thumbs-prev]', this);
      const next = $('[data-cv2-thumbs-next]', this);
      if (!rail || !prev || !next) return;
      const overflow = rail.scrollWidth - rail.clientWidth > 3;
      prev.hidden = !overflow;
      next.hidden = !overflow;
      prev.disabled = !overflow || rail.scrollLeft <= 2;
      next.disabled = !overflow || rail.scrollLeft >= rail.scrollWidth - rail.clientWidth - 2;
      $('.cv2-gallery__rail-wrap', this)?.classList.toggle('has-overflow', overflow);
    }

    scrollThumbs(direction) {
      $('.cv2-gallery__thumbs', this)?.scrollBy({ left: direction * 260, behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
    }

    currentItem() {
      return this.items.find((item) => item.dataset.mediaId === this.activeMediaId) || this.items[this.index] || null;
    }

    openModal() {
      const modal = $('[data-cv2-gallery-modal]', this);
      if (!modal || !this.currentItem() || modal.open) return;
      this.modalReturnFocus = document.activeElement;
      this.refreshModal();
      modal.showModal();
      requestAnimationFrame(() => $('[data-cv2-gallery-close]', modal)?.focus({ preventScroll: true }));
    }

    refreshModal() {
      const modalItems = this.modalItems;
      let item = this.currentItem();
      if (item?.dataset.mediaType !== 'image') item = modalItems[0] || null;
      const modalImage = $('[data-cv2-modal-image]', this);
      if (!item || !modalImage) return;
      modalImage.src = item.dataset.previewSrc || $('img.cv2-gallery__image', item)?.currentSrc || '';
      modalImage.alt = item.dataset.previewAlt || $('img.cv2-gallery__image', item)?.alt || '';
      const modalCurrent = $('[data-cv2-modal-current]', this);
      if (modalCurrent) modalCurrent.textContent = String(Math.max(0, modalItems.findIndex((candidate) => candidate === item)) + 1);
    }
  }

  if (!customElements.get('corsaille-media-gallery')) {
    customElements.define('corsaille-media-gallery', CorsailleMediaGallery);
  }

  function optionEntries(root) {
    return $$('variant-selects .csp-option[data-option-name]', root).map((field) => {
      const input = $('input[type="radio"]:checked, select', field);
      return {
        name: field.dataset.optionName || input?.dataset.optionName || '',
        value: input?.value || '',
      };
    });
  }

  function optionValue(entries, tokens) {
    return entries.find((entry) => tokens.some((token) => normalize(entry.name).includes(token)))?.value || '';
  }

  function selectedVariant(root) {
    try {
      return JSON.parse($('variant-selects [data-selected-variant]', root)?.textContent || $('[data-cv2-selected-variant]', root)?.textContent || 'null');
    } catch (_) {
      return null;
    }
  }

  function publicPriceScopeLabel(root) {
    const priceScope = root.dataset.productPriceScope || 'unknown';
    if (priceScope === 'complete') return 'Preț complet';
    if (priceScope === 'setting-only') return 'Prețul monturii';
    if (priceScope === 'from') return 'De la';
    return 'Prețul configurației selectate';
  }

  function sync(root, variant = null) {
    clearTimeout(root.cv2VariantTimer);
    root.classList.remove('is-variant-updating');
    root.setAttribute('aria-busy', 'false');
    const entries = optionEntries(root);
    const metal = optionValue(entries, ['metal', 'material']);
    const stone = optionValue(entries, ['diamant', 'diamond', 'piatr', 'stone', 'culoare']);
    const carat = optionValue(entries, ['carat', 'ct']);
    const currency = root.dataset.currency || 'RON';

    if (variant?.price != null) {
      const formatted = money(variant.price, currency);
      const savedPriceLabel = publicPriceScopeLabel(root);
      const savedPrice = `${savedPriceLabel}: ${formatted}`;
      $$('[data-cv2-price],[data-cv2-summary-price],[data-corsaille-submit-price]:not([data-cv2-configure-price])', root)
        .forEach((node) => { node.textContent = formatted; });
      $$('[data-cv2-configure-price]', root)
        .forEach((node) => { node.textContent = `${savedPriceLabel} · ${formatted}`; });
      $$('[data-cv2-dock-price]', root.cv2Dock || root).forEach((node) => { node.textContent = formatted; });
      const submitLabel = $('[data-corsaille-submit-label]', root);
      if (submitLabel && variant.available !== false) {
        submitLabel.innerHTML = `COMANDĂ — <span data-corsaille-submit-price>${formatted}</span>`;
      }
      root.dataset.productPriceCents = String(variant.price);
      $$('[data-corsaille-save]', root).forEach((button) => {
        button.dataset.productPrice = savedPrice;
        const variantImage = variant.featured_media?.preview_image?.src;
        if (variantImage) button.dataset.productImage = variantImage;
      });
    }

    $$('variant-selects .csp-option[data-option-name]', root).forEach((field) => {
      const input = $('input[type="radio"]:checked, select', field);
      const values = $$('[data-selected-value],[data-csp-metal-selected-name]', field);
      if (input) values.forEach((value) => { value.textContent = input.value; });
    });

    if (metal) $$('[data-cv2-property-metal]', root).forEach((node) => { node.value = metal; });
    if (stone) $$('[data-cv2-property-stone]', root).forEach((node) => { node.value = stone; });
    if (carat) $$('[data-cv2-property-carat]', root).forEach((node) => { node.value = carat; });

    if (root.dataset.productProductionTime === 'available-now') {
      const productionUnavailable = !variant || variant.available === false;
      const productionLabel = productionUnavailable
        ? 'Solicită termenul pentru această configurație'
        : 'Disponibilă acum';
      $$('[data-cv2-production-label]', root).forEach((node) => { node.textContent = productionLabel; });
      $$('[data-cv2-property-lead-time]', root).forEach((node) => {
        node.value = productionUnavailable ? '' : 'Disponibilă acum';
        node.disabled = productionUnavailable;
      });
    }

    $$('[data-cv2-property-code]', root).forEach((node) => {
      const prefix = node.dataset.codePrefix || root.dataset.productId;
      node.value = `${prefix}-${variant?.id || $('input[name="id"]', root)?.value || ''}`;
    });

    const currentCarat = carat || $('[data-cv2-property-carat]', root)?.value || root.dataset.productCarat;
    const currentMetal = metal || $('[data-cv2-property-metal]', root)?.value || root.dataset.productMetal;
    const currentStone = stone || $('[data-cv2-property-stone]', root)?.value || root.dataset.productColor;
    const summaryParts = [currentCarat, root.dataset.productCut, currentStone, currentMetal, root.dataset.productSoldAs].filter(Boolean);
    const summaryText = summaryParts.join(' · ');
    const summary = $('[data-cv2-summary-options]', root);
    if (summary && summaryText) summary.textContent = summaryText;
    const dockParts = [currentCarat, currentMetal, publicPriceScopeLabel(root)].filter(Boolean);
    const dockOptions = $('[data-cv2-dock-options]', root.cv2Dock || root);
    if (dockOptions && dockParts.length) dockOptions.textContent = dockParts.join(' · ');

    if (metal && $('[data-cv2-spec-metal]', root)) $('[data-cv2-spec-metal]', root).textContent = metal;
    if (metal && $('[data-cv2-identity-metal]', root)) $('[data-cv2-identity-metal]', root).textContent = metal;
    if (stone && $('[data-cv2-spec-stone]', root)) $('[data-cv2-spec-stone]', root).textContent = stone;
    if (carat && $('[data-cv2-spec-carat]', root)) $('[data-cv2-spec-carat]', root).textContent = carat;
    const gallery = $('corsaille-media-gallery', root);
    const featuredMediaId = variant?.featured_media?.id
      ? `${root.dataset.section}-${variant.featured_media.id}`
      : '';
    gallery?.setVariant?.(variant?.id || '', featuredMediaId, {
      metal: currentMetal,
      stone: currentStone,
    });

    const unavailable = !variant || variant.available === false;
    const mainButton = $('[data-corsaille-add-to-cart], .product-form__submit', root);
    const submitLabel = $('[data-corsaille-submit-label]', root);
    const variantInput = $('input.product-variant-id[name="id"], input[name="id"]', root);
    if (variantInput && variant?.id) variantInput.value = String(variant.id);
    if (variantInput) variantInput.disabled = unavailable;
    if (mainButton) {
      mainButton.disabled = unavailable;
      mainButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      mainButton.dataset.available = unavailable ? 'false' : 'true';
    }
    if (submitLabel) {
      if (unavailable) {
        submitLabel.textContent = 'INDISPONIBIL';
      } else if (variant?.price != null) {
        const formatted = money(variant.price, currency);
        submitLabel.replaceChildren(document.createTextNode('COMANDĂ — '));
        const priceNode = document.createElement('span');
        priceNode.dataset.corsailleSubmitPrice = '';
        priceNode.textContent = formatted;
        submitLabel.append(priceNode);
      }
    }

    const dockButton = $('[data-cv2-dock-submit]', root.cv2Dock || root);
    if (dockButton) {
      dockButton.disabled = unavailable;
      dockButton.setAttribute('aria-disabled', unavailable ? 'true' : 'false');
      const dockLabel = $('[data-cv2-dock-action-label]', dockButton);
      if (dockLabel) dockLabel.textContent = unavailable ? 'Indisponibil' : 'Comandă';
    }
  }

  function initSize(root, signal) {
    const size = $('[data-cv2-size]', root);
    if (!size || size.dataset.ready === 'true') return;
    size.dataset.ready = 'true';
    const summarySize = $('[data-cv2-summary-size]', root);
    const personNodes = $$('[data-cv2-size-person]', size);
    const isPair = size.dataset.pairSizing === 'true' || personNodes.length > 1;
    let activePersonIndex = 0;
    let states = [];

    const syncSummary = () => {
      if (!summarySize || !states.length) return;
      if (isPair) {
        summarySize.textContent = `Mărimi · ${states.map((state) => `${state.label}: ${state.displayValue}`).join(' · ')}`;
      } else {
        summarySize.textContent = `Mărime · ${states[0].displayValue}`;
      }
    };

    states = personNodes.map((person, personIndex) => {
      const property = $('[data-cv2-property-size]', person);
      const result = $('[data-cv2-size-result]', person);
      const known = $('[data-cv2-known-size]', person);
      const type = $('[data-cv2-measure-type]', person);
      const measure = $('[data-cv2-measure-value]', person);
      const label = person.dataset.personLabel || `Inelul ${personIndex + 1}`;
      const state = {
        label,
        mode: $('[data-cv2-size-mode].is-active', person)?.dataset.cv2SizeMode || 'later',
        displayValue: 'de confirmat',
      };

      const setResult = (message = '', status = '') => {
        [known, measure].forEach((field) => field?.setAttribute('aria-invalid', 'false'));
        if (result) {
          result.textContent = message;
          result.classList.toggle('is-error', status === 'error');
          result.classList.toggle('is-valid', status === 'valid');
        }
        const activeField = state.mode === 'known' ? known : (state.mode === 'measure' ? measure : null);
        if (status === 'error') activeField?.setAttribute('aria-invalid', 'true');
      };

      state.validate = (focus = false, silent = false) => {
        if (!property) return true;
        if (state.mode === 'later') {
          property.value = 'De confirmat';
          state.displayValue = 'de confirmat';
          setResult();
          syncSummary();
          return true;
        }

        if (state.mode === 'known') {
          const raw = known?.value.trim() || '';
          const value = parseLocaleNumber(raw);
          if (!raw || !Number.isInteger(value) || value < 44 || value > 76) {
            property.value = '';
            state.displayValue = 'de completat';
            if (silent) setResult();
            else setResult(raw ? 'Introdu o mărime europeană între 44 și 76.' : 'Introdu mărimea europeană înainte de comandă.', 'error');
            syncSummary();
            if (focus) known?.focus();
            return false;
          }
          property.value = `EU ${value}`;
          state.displayValue = `EU ${value}`;
          setResult(`Mărime selectată: EU ${value}.`, 'valid');
          syncSummary();
          return true;
        }

        const raw = measure?.value.trim() || '';
        const value = parseLocaleNumber(raw);
        const isDiameter = type?.value === 'diameter';
        const min = isDiameter ? 14 : 44;
        const max = isDiameter ? 24.2 : 76;
        if (!raw || !Number.isFinite(value) || value < min || value > max) {
          property.value = '';
          state.displayValue = raw ? 'de corectat' : 'de completat';
          if (silent) setResult();
          else setResult(!raw ? 'Introdu măsurarea înainte de comandă.' : (isDiameter ? 'Diametrul plauzibil este între 14,0 și 24,2 mm.' : 'Circumferința plauzibilă este între 44 și 76 mm.'), 'error');
          syncSummary();
          if (focus) measure?.focus();
          return false;
        }
        const eu = Math.round(isDiameter ? value * Math.PI : value);
        property.value = `EU ${eu} — ${isDiameter ? `diametru ${value.toFixed(1)} mm` : `circumferință ${value.toFixed(1)} mm`}`;
        state.displayValue = `orientativ EU ${eu}`;
        setResult(`Recomandare orientativă: EU ${eu}. Verifică măsurarea înainte de comandă.`, 'valid');
        syncSummary();
        return true;
      };

      state.setMode = (mode, focus = true) => {
        state.mode = mode;
        activePersonIndex = personIndex;
        $$('[data-cv2-size-mode]', person).forEach((button) => {
          const active = button.dataset.cv2SizeMode === mode;
          button.classList.toggle('is-active', active);
          button.setAttribute('aria-pressed', String(active));
        });
        $$('[data-cv2-size-panel]', person).forEach((panel) => { panel.hidden = panel.dataset.cv2SizePanel !== mode; });
        setResult();
        state.validate(false, true);
        if (focus && mode !== 'later') requestAnimationFrame(() => (mode === 'known' ? known : measure)?.focus({ preventScroll: true }));
      };

      $$('[data-cv2-size-mode]', person).forEach((button) => button.addEventListener('click', () => state.setMode(button.dataset.cv2SizeMode), { signal }));
      known?.addEventListener('input', () => { known.value = known.value.replace(/[^0-9]/g, '').slice(0, 2); state.validate(false, true); }, { signal });
      known?.addEventListener('blur', () => state.validate(false, false), { signal });
      measure?.addEventListener('input', () => { measure.value = measure.value.replace(/[^0-9.,]/g, '').replace(/([.,].*)[.,]/g, '$1').slice(0, 5); state.validate(false, true); }, { signal });
      measure?.addEventListener('blur', () => state.validate(false, false), { signal });
      type?.addEventListener('change', () => {
        if (measure) measure.placeholder = type.value === 'diameter' ? 'de exemplu 16,6' : 'de exemplu 52';
        state.validate(false, true);
      }, { signal });
      person.addEventListener('focusin', () => { activePersonIndex = personIndex; }, { signal });
      return state;
    });

    const guide = $('[data-cv2-size-guide]', size);
    const guideOpen = $('[data-cv2-size-guide-open]', size);
    guideOpen?.addEventListener('click', () => {
      if (!guide || guide.open) return;
      guide.showModal();
      requestAnimationFrame(() => $('[data-cv2-size-guide-close]', guide)?.focus({ preventScroll: true }));
    }, { signal });
    $('[data-cv2-size-guide-close]', size)?.addEventListener('click', () => guide?.close(), { signal });
    $('[data-cv2-size-guide-use]', size)?.addEventListener('click', () => {
      guide?.close();
      states[activePersonIndex]?.setMode?.('measure');
    }, { signal });
    guide?.addEventListener('click', (event) => {
      if (event.target === guide) guide.close();
    }, { signal });
    guide?.addEventListener('close', () => {
      if (guideOpen?.isConnected) guideOpen.focus({ preventScroll: true });
    }, { signal });

    const form = document.getElementById(`product-form-${root.dataset.section}`);
    form?.addEventListener('submit', (event) => {
      const invalidIndex = states.findIndex((state) => !state.validate(false, false));
      if (invalidIndex >= 0) {
        event.preventDefault();
        event.stopImmediatePropagation();
        states[invalidIndex].validate(true, false);
      }
    }, { capture: true, signal });

    states.forEach((state) => state.validate(false, true));
    syncSummary();
  }

  function initDock(root, signal) {
    const dock = $('[data-cv2-dock]', root);
    if (!dock || dock.dataset.ready === 'true') return;
    dock.dataset.ready = 'true';

    const purchaseButton = $('[data-corsaille-add-to-cart], .product-form__submit', root);
    const start = $('[data-cv2-dock-start]', root);
    const end = $('[data-cv2-dock-end]', root);
    const dockButton = $('[data-cv2-dock-submit]', dock);
    const originalParent = dock.parentNode;
    const placeholder = document.createComment('corsaille-purchase-dock');
    originalParent?.insertBefore(placeholder, dock);
    document.body.appendChild(dock);
    root.cv2Dock = dock;

    let startPassed = false;
    let endApproaching = false;
    const stateFromGeometry = () => {
      const headerOffset = visibleHeaderOffset();
      const startRect = start?.getBoundingClientRect();
      const endRect = end?.getBoundingClientRect();
      const dockHeight = Math.max(dock.offsetHeight, 66);
      startPassed = Boolean(startRect && startRect.top <= headerOffset + 8);
      endApproaching = Boolean(endRect && endRect.top <= innerHeight - dockHeight - 18);
    };

    const hasVisibleModal = () => [...document.querySelectorAll('dialog[open], [aria-modal="true"]')].some((candidate) => {
      if (candidate.closest('[hidden]')) return false;
      const style = getComputedStyle(candidate);
      if (style.display === 'none' || style.visibility === 'hidden' || Number(style.opacity) === 0) return false;
      const rect = candidate.getBoundingClientRect();
      if (rect.width < 1 || rect.height < 1) return false;
      return rect.bottom > 0 && rect.right > 0 && rect.top < innerHeight && rect.left < innerWidth;
    });

    const render = () => {
      stateFromGeometry();
      const mobile = matchMedia('(max-width: 989px)').matches;
      const active = document.activeElement;
      const fieldFocused = root.contains(active) && /INPUT|SELECT|TEXTAREA/.test(active?.tagName || '');
      const modalOpen = hasVisibleModal();
      const drawerOpen = Boolean(document.querySelector('cart-drawer.active, .drawer.active, [data-cart-drawer][open]'));
      const unavailable = purchaseButton?.disabled || dockButton?.disabled;
      const viewport = window.visualViewport;
      const keyboardOpen = Boolean(viewport && innerHeight - viewport.height > 150);
      const visible = mobile && startPassed && !endApproaching && !fieldFocused && !modalOpen && !drawerOpen && !keyboardOpen && !unavailable;
      dock.classList.toggle('is-visible', visible);
      dock.setAttribute('aria-hidden', visible ? 'false' : 'true');
      dock.inert = !visible;
      document.documentElement.classList.toggle('corsaille-purchase-dock-visible', visible);
    };

    let queued = false;
    const request = () => {
      if (queued) return;
      queued = true;
      requestAnimationFrame(() => {
        queued = false;
        render();
      });
    };

    const observer = new IntersectionObserver(request, {
      root: null,
      rootMargin: '-1px 0px -1px 0px',
      threshold: [0, 1],
    });
    if (start) observer.observe(start);
    if (end) observer.observe(end);

    addEventListener('scroll', request, { passive: true, signal });
    addEventListener('resize', request, { passive: true, signal });
    window.visualViewport?.addEventListener('resize', request, { passive: true, signal });
    window.visualViewport?.addEventListener('scroll', request, { passive: true, signal });
    root.addEventListener('focusin', request, { signal });
    root.addEventListener('focusout', request, { signal });
    document.addEventListener('dialog:opened', request, { signal });
    document.addEventListener('dialog:closed', request, { signal });
    dockButton?.addEventListener('click', () => purchaseButton?.click(), { signal });
    signal.addEventListener('abort', () => {
      observer.disconnect();
      document.documentElement.classList.remove('corsaille-purchase-dock-visible');
      root.cv2Dock = null;
      dock.remove();
      placeholder.remove();
    }, { once: true });
    render();
  }

  function initProduct(root) {
    if (!root || root.dataset.cv2Ready === 'true') return;
    document.body.classList.add('cv2-product-page');
    root.dataset.cv2Ready = 'true';
    root.cv2AbortController?.abort();
    const controller = new AbortController();
    const signal = controller.signal;
    root.cv2AbortController = controller;

    initSize(root, signal);
    initDock(root, signal);
    syncProductViewport(root, true);
    const viewportSync = () => syncProductViewport(root, true);
    const headerSync = () => syncProductViewport(root, false);
    addEventListener('resize', viewportSync, { passive: true, signal });
    addEventListener('orientationchange', viewportSync, { passive: true, signal });
    let viewportQueued = false;
    addEventListener('scroll', () => {
      if (viewportQueued) return;
      viewportQueued = true;
      requestAnimationFrame(() => {
        viewportQueued = false;
        headerSync();
      });
    }, { passive: true, signal });
    const identityObserver = new ResizeObserver(viewportSync);
    identityObserver.observe($('.cv2-identity', root) || root);
    root.cv2IdentityObserver = identityObserver;
    signal.addEventListener('abort', () => identityObserver.disconnect(), { once: true });
    root.addEventListener('change', (event) => {
      if (!event.target.closest('variant-selects')) return;
      clearTimeout(root.cv2VariantTimer);
      root.classList.add('is-variant-updating');
      root.setAttribute('aria-busy', 'true');
      root.cv2VariantTimer = setTimeout(() => {
        root.classList.remove('is-variant-updating');
        root.setAttribute('aria-busy', 'false');
      }, 4500);
    }, { signal });

    $('[data-cv2-share]', root)?.addEventListener('click', async (event) => {
      const button = event.currentTarget;
      const data = {
        title: button.dataset.shareTitle || document.title,
        url: button.dataset.shareUrl || location.href,
      };
      try {
        if (navigator.share) {
          await navigator.share(data);
        } else {
          await navigator.clipboard.writeText(data.url);
          const label = $('span', button);
          const old = label?.textContent;
          if (label) label.textContent = 'Link copiat';
          setTimeout(() => { if (label) label.textContent = old; }, 1400);
        }
      } catch (error) {
        if (error?.name !== 'AbortError') {
          location.href = `mailto:?subject=${encodeURIComponent(data.title)}&body=${encodeURIComponent(data.url)}`;
        }
      }
    }, { signal });

    sync(root, selectedVariant(root));
  }

  function initScope(scope = document) {
    $$('[data-corsaille-product-v2]', scope).forEach(initProduct);
  }

  function destroyScope(scope = document) {
    $$('[data-corsaille-product-v2]', scope).forEach((root) => {
      root.cv2AbortController?.abort();
      root.cv2IdentityObserver?.disconnect();
      clearTimeout(root.cv2VariantTimer);
      const size = $('[data-cv2-size]', root);
      const dock = root.cv2Dock || $('[data-cv2-dock]', root);
      if (size) delete size.dataset.ready;
      if (dock) delete dock.dataset.ready;
      delete root.dataset.cv2Ready;
    });
    queueMicrotask(() => {
      if (!document.querySelector('[data-corsaille-product-v2]')) {
        document.body.classList.remove('cv2-product-page');
      }
    });
  }

  document.addEventListener('DOMContentLoaded', () => initScope());
  document.addEventListener('shopify:section:load', (event) => initScope(event.target));
  document.addEventListener('shopify:section:unload', (event) => destroyScope(event.target));
  document.addEventListener('product-info:loaded', (event) => {
    initProduct(event.target.closest?.('[data-corsaille-product-v2]') || event.target);
  });

  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
    subscribe(PUB_SUB_EVENTS.variantChange, ({ data }) => {
      const sectionId = String(data.sectionId || '');
      const root = document.querySelector(`[data-corsaille-product-v2][data-section="${CSS.escape(sectionId)}"]`);
      if (root) sync(root, data.variant);
    });
  }
})();
